import Graph from 'graph-data-structure';
import { scopesGraphUtils } from './scopesGraphUtils';

type NodeId = string;
type EdgeWeight = number;
interface Serialized {
  nodes: {
    id: NodeId;
  }[];
  links: {
    source: NodeId;
    target: NodeId;
    weight: EdgeWeight;
  }[];
}

export type GraphStructure = {
  addNode: (node: NodeId) => unknown;
  removeNode: (node: NodeId) => unknown;
  nodes: () => NodeId[];
  adjacent: (node: NodeId) => NodeId[];
  addEdge: (u: NodeId, v: NodeId, weight?: number | undefined) => unknown;
  removeEdge: (u: NodeId, v: NodeId) => unknown;
  hasEdge: (u: NodeId, v: NodeId) => boolean;
  setEdgeWeight: (u: NodeId, v: NodeId, weight: EdgeWeight) => unknown;
  getEdgeWeight: (u: NodeId, v: NodeId) => EdgeWeight;
  indegree: (node: NodeId) => number;
  outdegree: (node: NodeId) => number;
  depthFirstSearch: (sourceNodes?: string[] | undefined, includeSourceNodes?: boolean, errorOnCycle?: boolean) => string[];
  hasCycle: () => boolean;
  lowestCommonAncestors: (node1: NodeId, node2: NodeId) => string[];
  topologicalSort: (sourceNodes?: string[] | undefined, includeSourceNodes?: boolean) => string[];
  shortestPath: (
    source: NodeId,
    destination: NodeId,
  ) => string[] & {
    weight?: number | undefined;
  };
  serialize: () => Serialized;
  deserialize: (serialized: Serialized) => unknown;
};

export const createScopesGraph = (scopes: CF.API.Scopes.Scope[], endpoints: CF.API.Scopes.Endpoint[]): GraphStructure => {
  const graph: GraphStructure = Graph();
  scopes.forEach((scope) => addPermissionToGraph(graph, scope.scope, scope.depending_scopes));
  endpoints.forEach((endpoint) => addPermissionToGraph(graph, endpoint.endpoint, endpoint.depending_scopes));

  return graph;
};

const addPermissionToGraph = (graph: GraphStructure, permissionName: string, dependentScopes?: string[]): void => {
  const parentName = scopesGraphUtils.getCleanedParentName(permissionName);

  // If the scope isn't a child or has no depending scopes,
  // it must be the root of a subtree (i.e: scope.scope is
  // Predict, Search, Inputs, Concepts, Models).
  if (!dependentScopes && parentName !== 'All') {
    scopesGraphUtils.addDependencyOrReverseDependencies(graph, permissionName, parentName);
  }

  // Otherwise, we're looking at a subtree that is not bi-directional.
  // Grab dependent scopes, if they exist.
  if (dependentScopes) {
    // For every dependent scope in the current scope, add it as
    // a neighbor to it's parent.
    // [1] Here we add a link from the All node to the dependency.
    // [2] And from this scopeName to the dependency.
    for (const dependency of dependentScopes) {
      graph.addEdge('All', dependency);
      graph.addEdge(permissionName, dependency);
    }

    scopesGraphUtils.addDependencyOrReverseDependencies(graph, permissionName, parentName);
  }
};
